import { fDateTime } from 'src/utils/format-time';
import { endpoints, axiosInstances } from 'src/utils/axios';

import {
  GetOrdersParams,
  GetPendingParams,
  GetProductsParams,
  GetOrderByIdResponse,
  UpdateMissingPending,
  GetGroupedOrdersResponse,
  GetOrderSuppliersResponse,
  GetGroupedProductsPayload,
  GetCampaignProductsParams,
  GetGroupedProductsResponse,
  MarkOrderAsDeliveryPayload,
  GetCampaignProductsResponse,
  MenloShopsCreateOrderPayload,
  GetGroupedProductsUpdatePaymentMethodPayload,
} from 'src/types/menloshops';

export async function getProducts({
  pageNumber,
  pageSize,
  supplier,
  name,
  ActiveProduct,
}: GetProductsParams) {
  const urlParams = new URLSearchParams({
    pageNumber,
    pageSize,
    supplier: supplier ?? '',
    name: name ?? '',
    ActiveProduct: ActiveProduct ?? '',
  }).toString();

  const pathname = `${endpoints.menloShops.products.list}?${urlParams}`;
  const res = await axiosInstances.notAuthenticated.get(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function getProductById(id: string) {
  const res = await axiosInstances.notAuthenticated.get(
    `${endpoints.menloShops.products.getById(id)}`
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function getOrders({
  Status,
  PaymentStatus,
  PaymentDate,
  pageNumber,
  pageSize,
}: GetOrdersParams) {
  const formattedDate = PaymentDate ? fDateTime(PaymentDate, 'yyyy-MM-dd') : null;

  const searchParams = new URLSearchParams({
    Status: Status ?? '',
    PaymentStatus: PaymentStatus ?? '',
    PaymentDate: formattedDate ?? '',
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
  }).toString();

  const pathname = `${endpoints.menloShops.order.list}?${searchParams}`;

  const res = await axiosInstances.notAuthenticated.get(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function getGroupedOrders({
  Status,
  PaymentStatus,
  PaymentDate,
  pageNumber,
  pageSize,
}: GetOrdersParams): Promise<GetGroupedOrdersResponse> {
  const formattedDate = PaymentDate ? fDateTime(PaymentDate, 'yyyy-MM-dd') : null;

  const searchParams = new URLSearchParams({
    Status: Status ?? '',
    PaymentStatus: PaymentStatus ?? '',
    PaymentDate: formattedDate ?? '',
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
  }).toString();

  const pathname = `${endpoints.menloShops.order.grouped}?${searchParams}`;
  const res = await axiosInstances.notAuthenticated.get(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export const getOrderSuppliers = async (groupId: string): Promise<GetOrderSuppliersResponse> => {
  const res = await axiosInstances.notAuthenticated.get(
    `${endpoints.menloShops.order.suppliers(groupId)}`
  );
  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export async function getOrderById(id: string): Promise<GetOrderByIdResponse> {
  const res = await axiosInstances.notAuthenticated(`${endpoints.menloShops.order.getById(id)}`);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function createOrder(payload: MenloShopsCreateOrderPayload) {
  const res = await axiosInstances.notAuthenticated.post(
    endpoints.menloShops.order.createOrder,
    payload
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export const getGroupedProductsRequest = async (
  payload: GetGroupedProductsPayload[],
  updatePaymentMethodPayload?: GetGroupedProductsUpdatePaymentMethodPayload
): Promise<GetGroupedProductsResponse[]> => {
  const searchParams = updatePaymentMethodPayload
    ? new URLSearchParams({
        supplierId: updatePaymentMethodPayload.supplierId,
        ...(updatePaymentMethodPayload?.paymentMethod && {
          paymentMethod: updatePaymentMethodPayload.paymentMethod,
        }),
      }).toString()
    : '';
  const res = await axiosInstances.notAuthenticated.post(
    `${endpoints.menloShops.products.grouped}?${searchParams}`,
    payload
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export const getSupplier = async (payload: any): Promise<any> => {
  const urlParams = new URLSearchParams({ ...payload });

  const res = await axiosInstances.authenticated.get(
    `${endpoints.menloShops.suppliers.list}?${urlParams.toString()}`
  );
  if (res === undefined || res?.status === 500 || res?.status === 404) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export async function getPendings({
  pageNumber,
  pageSize,
  PendingStatus,
  PendingType,
  SupplierId,
}: GetPendingParams) {
  const urlParams = new URLSearchParams({
    PendingStatus: PendingStatus ?? '',
    PendingType: PendingType ?? '',
    SupplierId: SupplierId ?? '',
    pageNumber,
    pageSize,
  }).toString();

  const pathname = `${endpoints.menloShops.pending.list}?${urlParams}`;
  const res = await axiosInstances.authenticated.get(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function getMissingById(pendingId: string) {
  const res = await axiosInstances.authenticated.get(
    `${endpoints.menloShops.pending.getMissingById(pendingId)}`
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export async function getApprovalById(pendingId: string) {
  const res = await axiosInstances.authenticated.get(
    `${endpoints.menloShops.pending.getApprovalById(pendingId)}`
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}

export const updateApprovalPendig = async (status: string, pendingId: string) => {
  const urlParams = new URLSearchParams({
    status,
  }).toString();

  const pathname = `${endpoints.menloShops.pending.patchStatus(pendingId)}?${urlParams}`;
  const res = await axiosInstances.authenticated.patch(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export const updateMissing = async (payload: UpdateMissingPending) => {
  const res = await axiosInstances.authenticated.put(
    endpoints.menloShops.pending.updateMissing,
    payload
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export const markOrderAsDelivered = async (payload: MarkOrderAsDeliveryPayload) => {
  const res = await axiosInstances.authenticated.post(
    endpoints.menloShops.order.markOrderAsDelivered,
    payload
  );

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
};

export async function getCampaignProducts({
  pageNumber,
  pageSize,
  PromotionName,
  IncludeInactive,
}: GetCampaignProductsParams): Promise<GetCampaignProductsResponse> {
  const urlParams = new URLSearchParams({
    pageNumber,
    pageSize,
    PromotionName: PromotionName ?? '',
    ActiveProduct: IncludeInactive ?? '',
  }).toString();

  const pathname = `${endpoints.menloShops.productsCampaign.list}?${urlParams}`;
  const res = await axiosInstances.notAuthenticated.get(pathname);

  if (res === undefined || res?.status !== 200) {
    throw new Error(res?.statusText);
  }

  return res.data;
}
